import { useState } from 'react';
import type { LaadwensenData, DLBInteresse, Installatietermijn } from '@shared/types';

interface Props {
  state: LaadwensenData;
  onChange: (data: LaadwensenData) => void;
  onNext: () => void;
  onBack: () => void;
  returnToSamenvatting?: boolean;
  onReturnToSamenvatting?: () => void;
}

const DLB_UITLEG = `Dynamic Load Balancing (DLB) zorgt ervoor dat je laadpaal automatisch minder stroom gebruikt als andere apparaten in huis aanstaan. Zo voorkom je overbelasting zonder je aansluiting te hoeven verzwaren. De meeste moderne laadpalen ondersteunen dit.`;

export default function StapLaadwensen({ state, onChange, onNext, onBack, returnToSamenvatting, onReturnToSamenvatting }: Props) {
  const [errors, setErrors] = useState<Partial<Record<keyof LaadwensenData, string>>>({});
  const [toonDlbUitleg, setToonDlbUitleg] = useState(false);

  function validate(): boolean {
    const e: Partial<Record<keyof LaadwensenData, string>> = {};
    if (!state.dlbInteresse) e.dlbInteresse = 'Geef aan of je interesse hebt in DLB.';
    if (!state.installatietermijn) e.installatietermijn = 'Selecteer wanneer je de laadpaal wil installeren.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (validate()) onNext();
  }

  return (
    <form className="lc-stap lc-stap--laadwensen" onSubmit={handleSubmit} noValidate>
      <h2 className="lc-stap__titel">Laadwensen</h2>
      <p className="lc-stap__beschrijving">
        Geef aan hoe je de laadpaal wil gebruiken. We nemen dit mee in ons advies.
      </p>

      {/* DLB interesse — A5: toggle knop i.p.v. "Wat is dat?" radio-optie */}
      <fieldset className="lc-veld lc-veld--radio-groep">
        <div className="lc-veld__legend-row">
          <legend className="lc-veld__label">
            Interesse in Dynamic Load Balancing? <span aria-hidden="true">*</span>
          </legend>
          <button
            type="button"
            className="lc-knop lc-knop--info-toggle"
            aria-expanded={toonDlbUitleg}
            onClick={() => setToonDlbUitleg((v) => !v)}
          >
            {toonDlbUitleg ? '▲ Verberg uitleg' : 'Wat is dat?'}
          </button>
        </div>
        {toonDlbUitleg && (
          <div className="lc-info-blok lc-info-blok--compact">
            <p>{DLB_UITLEG}</p>
          </div>
        )}
        {(
          [
            { waarde: 'ja', label: 'Ja, ik wil dat de laadpaal zich aanpast aan mijn verbruik' },
            { waarde: 'nee', label: 'Nee' },
          ] as { waarde: DLBInteresse; label: string }[]
        ).map(({ waarde, label }) => (
          <label key={waarde} className="lc-radio">
            <input
              type="radio"
              name="dlbInteresse"
              value={waarde}
              checked={state.dlbInteresse === waarde}
              onChange={() => onChange({ ...state, dlbInteresse: waarde })}
            />
            <span>{label}</span>
          </label>
        ))}
        {errors.dlbInteresse && (
          <p className="lc-veld__fout" role="alert">{errors.dlbInteresse}</p>
        )}
      </fieldset>

      {/* Installatietermijn */}
      <fieldset className="lc-veld lc-veld--radio-groep">
        <legend className="lc-veld__label">
          Wanneer wil je de laadpaal installeren? <span aria-hidden="true">*</span>
        </legend>
        {(
          [
            { waarde: 'binnen_1_maand', label: 'Binnen 1 maand' },
            { waarde: 'binnen_3_maanden', label: 'Binnen 3 maanden' },
            { waarde: 'binnen_6_maanden', label: 'Binnen 6 maanden' },
            { waarde: 'orienteren', label: 'Ik oriënteer me nog' },
          ] as { waarde: Installatietermijn; label: string }[]
        ).map(({ waarde, label }) => (
          <label key={waarde} className="lc-radio">
            <input
              type="radio"
              name="installatietermijn"
              value={waarde}
              checked={state.installatietermijn === waarde}
              onChange={() => onChange({ ...state, installatietermijn: waarde })}
            />
            <span>{label}</span>
          </label>
        ))}
        {errors.installatietermijn && (
          <p className="lc-veld__fout" role="alert">{errors.installatietermijn}</p>
        )}
      </fieldset>

      {/* VvE */}
      <fieldset className="lc-veld lc-veld--radio-groep">
        <legend className="lc-veld__label">Is dit voor een VvE-situatie?</legend>
        {([
          { waarde: true, label: 'Ja' },
          { waarde: false, label: 'Nee' },
        ] as { waarde: boolean; label: string }[]).map(({ waarde, label }) => (
          <label key={String(waarde)} className="lc-radio">
            <input
              type="radio"
              name="isVVE"
              checked={state.isVVE === waarde}
              onChange={() =>
                onChange({ ...state, isVVE: waarde, vveAantalParkeerplekken: waarde ? (state.vveAantalParkeerplekken ?? 1) : null })
              }
            />
            <span>{label}</span>
          </label>
        ))}
        {state.isVVE && (
          <div className="lc-veld lc-veld--inline">
            <label className="lc-veld__label" htmlFor="lc-vve-plekken">Hoeveel parkeerplekken?</label>
            <input
              id="lc-vve-plekken"
              className="lc-veld__input lc-veld__input--kort"
              type="number"
              min={1}
              max={50}
              value={state.vveAantalParkeerplekken ?? 1}
              onChange={(e) => onChange({ ...state, vveAantalParkeerplekken: parseInt(e.target.value) || 1 })}
            />
          </div>
        )}
      </fieldset>

      <div className="lc-stap__acties">
        {returnToSamenvatting && (
          <button type="button" className="lc-knop lc-knop--terug-samenvatting" onClick={onReturnToSamenvatting}>
            ← Terug naar samenvatting
          </button>
        )}
        <button type="button" className="lc-knop lc-knop--terug" onClick={onBack}>
          ← Terug
        </button>
        <button type="submit" className="lc-knop lc-knop--primair">
          {returnToSamenvatting ? 'Opslaan & terug →' : 'Volgende →'}
        </button>
      </div>
    </form>
  );
}
